import noimage from 'assets/images/noimage.png'
import LoadingV2 from 'components/LoadingV2'
import { capitalizeEveryWord } from 'helpers'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { Download, X } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  Row,
  Spinner,
} from 'reactstrap'
import { formatDateOnlyPMBV1 } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import HistoryLine from 'components/HistoryLine'
import {
  useFetchLinkTagsQuery,
  useFetchProjectByIdQuery,
  useSharedProjectDetailQuery,
  useListResourcesAccessQuery,
  useRevokeAccessToProjectMutation,
  useFetchProjectLogsByIdQuery,
} from './projectsMenuListApiSlice'

const ProjectDetailTab = ({
  projectId,
  onDelete,
  onClose,
  onShare,
  isShared = false,
  canEdit = true,
}) => {
  const navigate = useNavigate()
  const [isOpenRevoke, setIsOpenRevoke] = useState(false)
  const [revokeUsers, setRevokeUsers] = useState([])
  const {
    data: ownData,
    isLoading: isLoadingOwn,
    refetch: refetchOwn,
  } = useFetchProjectByIdQuery(
    { projectId },
    { skip: !projectId || isShared },
  )

  const { data: sharedData, isLoading: isLoadingShared } =
    useSharedProjectDetailQuery(
      { id: projectId },
      { skip: !projectId || !isShared },
    )

  const data = isShared ? sharedData : ownData
  const isLoadingProjectById = isShared ? isLoadingShared : isLoadingOwn

  const { data: resourceAccessData, refetch: refetchAccess } =
    useListResourcesAccessQuery(
      { resourceId: projectId, resourceType: 'PROJECT' },
      { skip: !projectId },
    )

  const { data: projectLogsData, isLoading: isLoadingLogs } =
    useFetchProjectLogsByIdQuery(
      { projectId },
      { skip: !projectId },
    )

  const [revokeAccess, { isLoading: isRevoking }] =
    useRevokeAccessToProjectMutation()

  const handleRevoke = async () => {
    if (!revokeUsers || revokeUsers.length === 0) return
    try {
      await revokeAccess({
        resourceType: 'PROJECT',
        resourceId: projectId,
        userIds: revokeUsers.map((u) => u.userId),
      }).unwrap()
      enqueueSnackbar('Akses berhasil dihapus.', { variant: 'success' })
      setIsOpenRevoke(false)
      setRevokeUsers([])
      if (refetchAccess) refetchAccess()
      if (refetchOwn) refetchOwn()
    } catch (error) {
      enqueueSnackbar(
        error?.data?.meta?.message ||
        error?.data?.message ||
        'Gagal menghapus akses',
        { variant: 'error' },
      )
    }
  }

  const { data: linkTagsData, isLoading: isLoadingLinkTags } =
    useFetchLinkTagsQuery(
      { linkDocs: data?.linkDocs, page: 0, size: 50 },
      { skip: !data?.linkDocs },
    )

  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: { label: 'Draft', badgeClass: 'bg-warning-50 text-warning-700' },
      ACTIVE: { label: 'Aktif', badgeClass: 'bg-success-50 text-success-700' },
      CLOSED: { label: 'Closed', badgeClass: 'bg-danger-50 text-danger-700' },
    }
    return (
      statusConfig[status] || {
        label: status,
        badgeClass: 'bg-gray-100 text-gray-700',
      }
    )
  }

  const normalizeParticipants = (participantsList) => {
    if (!participantsList) return []
    const list = Array.isArray(participantsList)
      ? participantsList
      : typeof participantsList === 'string' && participantsList.trim()
      ? [{ name: participantsList, role: 'PARTICIPANT' }]
      : []

    return list.filter((p) => p && p.role !== 'OWNER')
  }

  // Combine participants from resourceAccessData and data.participants to ensure all assignees appear
  const combinedParticipants = (() => {
    const listA = normalizeParticipants(resourceAccessData?.participants)
    const listB = normalizeParticipants(data?.participants)

    const map = new Map()
    listB.forEach((p) => {
      const key = p.userId || p.id || p.name
      if (key) map.set(String(key), p)
    })
    listA.forEach((p) => {
      const key = p.userId || p.id || p.name
      if (key) map.set(String(key), p)
    })
    return Array.from(map.values())
  })()

  const dataParticipants = combinedParticipants

  const statusBadge = getStatusBadge(data?.status)

  const isDraft = data?.status === 'DRAFT'
  const isActive = data?.status === 'ACTIVE'

  if (isLoadingProjectById) {
    return <LoadingV2 />
  }

  return (
    <Row className="pb-4">
      {canEdit && (
        <div className="d-flex flex-wrap gap-2 justify-content-end mb-3">
          <Button
            outline
            size="sm"
            color="primary"
            className="d-flex justify-content-center w-md-auto text-nowrap"
            onClick={() =>
              navigate(
                `/projects/edit/${projectId}` +
                (isShared ? '?shared=true' : ''),
                {
                  state: { from: 'detail' },
                },
              )
            }
          >
            Ubah
          </Button>
          {onClose && (
            <Button
              disabled={!isActive}
              outline
              size="sm"
              color="primary"
              className="d-flex justify-content-center w-md-auto text-nowrap"
              onClick={onClose}
            >
              Tutup
            </Button>
          )}
          {!isShared && onShare && (
            <Button
              outline
              size="sm"
              color="primary"
              className="d-flex justify-content-center w-md-auto text-nowrap"
              onClick={onShare}
            >
              Bagikan
            </Button>
          )}
          {onDelete && (
            <Button
              disabled={!isDraft}
              outline
              size="sm"
              color="danger"
              className="d-flex justify-content-center w-md-auto text-nowrap"
              onClick={onDelete}
            >
              Hapus
            </Button>
          )}
        </div>
      )}
      <Col md="6">
        <Card className="shadow-sm border-0 mb-3">
          <CardBody>
            <h6 className="fw-semibold mb-3">Informasi Project</h6>
            <Row className="mb-3">
              <Col xs="6" className="fw-light text-sm">
                Judul
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                {data?.title || '-'}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs="6" className="fw-light text-sm">
                Nomor
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                {data?.label || '-'}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs="6" className="fw-light text-sm">
                Deskripsi
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                {data?.description || '-'}
              </Col>
            </Row>


            <Row className="mb-3">
              <Col xs="6" className="fw-light text-sm">
                Status
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                <div className={`badge rounded-pill ${statusBadge.badgeClass}`}>
                  {capitalizeEveryWord(statusBadge.label)}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs="6" className="fw-light text-sm">
                Penerima Tugas
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                {data?.assigneeUser || '-'}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs="6" className="fw-light text-sm">
                Tanggal Mulai
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                {data?.startDate ? formatDateOnlyPMBV1(data.startDate) : '-'}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs="6" className="fw-light text-sm">
                Tanggal Selesai
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                {data?.endDate ? formatDateOnlyPMBV1(data.endDate) : '-'}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs="6" className="fw-light text-sm">
                Kepemilikan
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                {data?.owner || '-'}
              </Col>
            </Row>

            <Row className="mb-0">
              <Col xs="6" className="fw-light text-sm">
                Tanggal Buat
              </Col>
              <Col xs="6" className="fw-semibold text-sm">
                {data?.createdAt ? formatDateOnlyPMBV1(data.createdAt) : '-'}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {data?.linkDocs && (
          <Card className="shadow-sm border-0 mb-3">
            <CardBody>
              <h6 className="fw-semibold mb-3">Dokumen terkait</h6>

              <div className="d-flex flex-column gap-2">
                {data.linkDocs.split(',').map((url, index) => {
                  const linkTag = linkTagsData?.items?.find(
                    (item) => item.linkName === url.trim(),
                  )
                  const displayName = linkTag?.tagName
                    ? `${linkTag.tagName}`
                    : `Dokumen ${index + 1}`

                  return (
                    <Button
                      key={index}
                      outline
                      color="primary"
                      className="d-flex align-items-center justify-content-between w-100"
                      onClick={() => window.open(url.trim(), '_blank')}
                      disabled={isLoadingLinkTags}
                    >
                      <div className="d-flex align-items-center">
                        <Download size={16} className="me-2" />
                        <span>Unduh {displayName}</span>
                      </div>
                    </Button>
                  )
                })}
              </div>
            </CardBody>
          </Card>
        )}
      </Col>

      <Col md="6">
        <Card className="shadow-sm border-0 mb-3">
          <CardBody>
            <h6 className="fw-semibold mb-3">
              Tags ({data?.tags?.length || 0})
            </h6>

            <Row className="mb-0">
              {data?.tags?.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {data.tags.map((t, idx) => (
                    <Badge
                      key={idx}
                      color="light"
                      className="px-3 py-2 border"
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#344054',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'left',
                      }}
                    >
                      {typeof t === 'object' ? t.tag : t}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div>Tidak ada tags</div>
              )}
            </Row>
          </CardBody>
        </Card>

        <Card className="shadow-sm border-0 mb-3">
          <CardBody>
            <h6 className="fw-semibold mb-3">
              Penerima Tugas ({dataParticipants?.length || 0})
            </h6>

            <Row className="mb-0">
              {dataParticipants?.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {dataParticipants.map((participant, index) => (
                    <Badge
                      key={index}
                      color="light"
                      className="px-3 py-2 border"
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#344054',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'left',
                      }}
                    >
                      {participant.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div>Tidak ada peserta</div>
              )}
            </Row>
          </CardBody>
        </Card>

        {!isShared && resourceAccessData?.participants?.length > 0 && (
          <Card className="shadow-sm border-0 mb-3">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">
                  Akses Dibagikan (
                  {
                    resourceAccessData.participants.filter(
                      (p) => p.role !== 'OWNER',
                    ).length
                  }
                  )
                </h6>
                {revokeUsers.length > 0 && (
                  <Button
                    size="sm"
                    color="danger"
                    onClick={() => setIsOpenRevoke(true)}
                  >
                    Hapus Akses ({revokeUsers.length})
                  </Button>
                )}
              </div>

              <div className="d-flex flex-column gap-2">
                {resourceAccessData.participants
                  .filter((p) => p.role !== 'OWNER')
                  .map((user, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
                    >
                      <div className="d-flex align-items-center gap-3">
                        <Input
                          type="checkbox"
                          className="m-0"
                          style={{ cursor: 'pointer' }}
                          checked={revokeUsers.some(
                            (u) => u.userId === user.userId,
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRevokeUsers((prev) => [...prev, user])
                            } else {
                              setRevokeUsers((prev) =>
                                prev.filter((u) => u.userId !== user.userId),
                              )
                            }
                          }}
                        />
                        <div className="d-flex flex-column">
                          <span
                            style={{
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              color: '#344054',
                            }}
                          >
                            {user.name}
                          </span>
                          <span
                            className="text-muted"
                            style={{ fontSize: '0.75rem' }}
                          >
                            {capitalizeEveryWord(user.role)}
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        color="link"
                        className="p-0 text-danger"
                        onClick={() => {
                          setRevokeUsers([user])
                          setIsOpenRevoke(true)
                        }}
                        disabled={isRevoking}
                        title="hapus akses"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ))}
              </div>
            </CardBody>
          </Card>
        )}

        <Card className="shadow-sm border-0 mb-3">
          <CardBody>
            <h6 className="fw-semibold mb-3">
              Target ({data?.goals?.length || 0})
            </h6>

            {data?.goals?.length > 0 ? (
              data.goals.map((goal, index) => (
                <Row
                  key={index}
                  className={`mb-3 ${index !== data.goals.length - 1 ? 'pb-3 border-bottom' : ''
                    }`}
                >
                  <Col
                    xs="6"
                    className="fw-light text-sm d-flex align-items-center gap-2"
                  >
                    <img
                      src={goal.linkFoto || noimage}
                      alt={goal.nameItem}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                    />
                    <span>{goal.nameItem}</span>
                  </Col>
                  <Col xs="6" className="fw-semibold text-sm text-md-end">
                    <span style={{ color: '#006386' }}>
                      {newFormatThousandSeparator(goal.amount || 0, 2)}{' '}
                      {capitalizeEveryWord(goal.unit)}
                    </span>
                  </Col>
                </Row>
              ))
            ) : (
              <div>Tidak ada target</div>
            )}
          </CardBody>
        </Card>

        <Card className="shadow-sm border-0 mb-3">
          <CardBody>
            <h6 className="fw-semibold mb-3">Riwayat Aktivitas</h6>
            {isLoadingLogs ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="primary" />
              </div>
            ) : projectLogsData?.items?.length > 0 || (Array.isArray(projectLogsData) && projectLogsData.length > 0) ? (
              <HistoryLine logs={projectLogsData?.items || projectLogsData} />
            ) : (
              <div>Belum ada riwayat aktivitas</div>
            )}
          </CardBody>
        </Card>
      </Col>

      <Modal
        className="p-4"
        centered
        isOpen={isOpenRevoke}
        toggle={() => {
          setIsOpenRevoke(false)
          setRevokeUsers([])
        }}
        autoFocus={false}
      >
        <ModalBody>
          <h5>Hapus Akses</h5>
          <p className="text-muted">
            Apakah Anda yakin ingin menghapus akses{' '}
            <strong>
              {revokeUsers.length === 1
                ? revokeUsers[0]?.name
                : `${revokeUsers.length} pengguna`}
            </strong>{' '}
            dari project <strong>{data?.title || data?.label || ''}</strong>?
          </p>
          <div className="d-flex gap-3">
            <Button
              onClick={() => {
                setIsOpenRevoke(false)
                setRevokeUsers([])
              }}
              disabled={isRevoking}
              color="secondary"
              outline
              block
              className="btn-cancel"
            >
              Batal
            </Button>
            <Button
              disabled={isRevoking}
              onClick={handleRevoke}
              block
              color="danger"
            >
              {isRevoking && <Spinner size="sm" className="me-2" />}
              Hapus Akses
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </Row>
  )
}

export default ProjectDetailTab
